import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import styled, { css } from 'styled-components';
import { Text } from 'components/primitives/typography';
import StarRating from 'components/pdp/v3/StarRating';
import { FlexAlignCenter, FlexAlignCenterSpaceBetween } from 'components/primitives/flex';
import { mediaMMdMin, mediaMSmMax, mediaXlMin } from 'helpers/breakpoints';
import AngleDown from 'assets/scss/fortawesome/icons/svg/angle-down.svg';
import UnarchiveIcon from 'assets/images/messaging/unarchive-action.svg';
import ArchiveIcon from 'assets/images/messaging/archive-action.svg';
import { getUserTypeColor } from 'utils/colors';
import Avatar from 'components/ui/Avatar/Avatar';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import Badge from 'components/ui/Badge/Badge';
import { determineStaticPreviewImage } from 'utils/images';
import useToggleMessageArchived from './hooks/useToggleMessageArchived';
import { activeTabNameAtom, mobileBookingPanelVisibleAtom, mobileConversationDetailVisibleAtom } from './store';

export default function ConversationHeader({
  hideBackButton,
  conversation,
  conversation: { booking },
}: {
  hideBackButton?: boolean;
  conversation: any;
}) {
  const [isMobileBookingPanelVisible, setMobileBookingPanelVisible] = useAtom(mobileBookingPanelVisibleAtom);
  const setMobileDetailVisible = useSetAtom(mobileConversationDetailVisibleAtom);
  const { toggleMessageArchived, archiveMessageMutation, archiveIntroductionModal } =
    useToggleMessageArchived(conversation);
  const activeTabName = useAtomValue(activeTabNameAtom);

  const conversationUser = booking?.user_type === 'renter' ? booking?.owner : booking?.renter;
  const viewerUserType = conversation.my_user_type_in_conversation;

  const isViewerRenter = viewerUserType === 'renter';
  const isViewerOwner = viewerUserType === 'owner';
  const isViewerCaptain = viewerUserType === 'captain';

  const rating =
    booking.renter.review_count > 0 ? (
      <StarRating rating={booking.renter.rating} reviewCount={booking.renter.review_count} short hideCountText />
    ) : null;

  const archiveButton = (
    <ArchiveButton
      data-testid="inbox-conversation-archive-btn"
      onClick={archiveMessageMutation.isLoading ? undefined : toggleMessageArchived}
    >
      {conversation.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
    </ArchiveButton>
  );

  return (
    <>
      <HeaderPanel>
        <FlexAlignCenter gap="16px">
          <Avatar
            diameter={48}
            profileLink={`/users/${conversationUser.id}`}
            src={determineStaticPreviewImage(conversationUser.picture.url, conversationUser.first_name)}
          />
          <FlexAlignCenterSpaceBetween flex="1">
            <div>
              <Text fSize="21px" fWeight="700">
                {conversationUser.first_name}
              </Text>
              <FlexAlignCenter gap="8px">
                {isViewerRenter && (
                  <>
                    <Text fSize="14px" fWeight="500">
                      Response rate
                    </Text>
                    <ResponseRate rate={booking?.owner.response_rate} />
                  </>
                )}
                {isViewerOwner && rating}
                {isViewerCaptain && rating}
              </FlexAlignCenter>
            </div>
            <Tooltip place="bottom" triggerWith={archiveButton}>
              {conversation.archived ? 'Unarchive conversation' : 'Archive conversation'}
            </Tooltip>
          </FlexAlignCenterSpaceBetween>
        </FlexAlignCenter>
      </HeaderPanel>

      <MobileHeaderPanel>
        {!hideBackButton ? (
          <BackButton
            onClick={() => {
              setMobileDetailVisible(false);
              setMobileBookingPanelVisible(false);
            }}
            $color={getUserTypeColor(activeTabName)}
          >
            <AngleDown width={16} className="angle-icon" /> Back
          </BackButton>
        ) : (
          <div />
        )}
        <FlexAlignCenter>
          {archiveButton}
          <Text
            fSize="14px"
            color="#5e696a"
            pt="4px"
            onClick={archiveMessageMutation.isLoading ? undefined : toggleMessageArchived}
          >
            {conversation.archived ? 'Unarchive' : 'Archive'}
          </Text>
        </FlexAlignCenter>
      </MobileHeaderPanel>

      <MobileShowBar>
        <FlexAlignCenter gap="6px" className="mdMinHide">
          <Text fSize="16px" fWeight="600">
            {conversationUser.first_name}
          </Text>

          {isViewerRenter && (
            <FlexAlignCenter gap="4px">
              <Text fSize="14px" fWeight="500">
                Response rate
              </Text>
              <ResponseRate rate={booking?.owner.response_rate} />
            </FlexAlignCenter>
          )}
          {isViewerOwner && rating}
          {isViewerCaptain && rating}
        </FlexAlignCenter>

        <SeeDetailsButton
          gap="6px"
          $isPanelVisible={isMobileBookingPanelVisible}
          onClick={() => setMobileBookingPanelVisible(!isMobileBookingPanelVisible)}
          $color={getUserTypeColor(activeTabName)}
        >
          {isMobileBookingPanelVisible ? 'Hide details' : 'See details'}
          <AngleDown width={16} className="angle-icon" />
        </SeeDetailsButton>
      </MobileShowBar>

      {archiveIntroductionModal}
    </>
  );
}

const panelStyles = css`
  border-bottom: 1px solid #dbdfe5;
  position: relative;
  z-index: 2;
  background-color: white;
`;

const HeaderPanel = styled.div`
  ${panelStyles};
  padding: 16px 32px;

  ${mediaMSmMax} {
    display: none;
  }
`;

const MobileHeaderPanel = styled(FlexAlignCenterSpaceBetween)`
  ${panelStyles};
  padding: 14px 20px;

  ${mediaMMdMin} {
    display: none;
  }
`;

const MobileShowBar = styled(FlexAlignCenterSpaceBetween)`
  box-shadow: 0 0 20px 0 rgb(0 0 0 / 10%);
  padding: 12px 20px;
  /* height: 55px; */

  .mdMinHide {
    ${mediaMMdMin} {
      display: none;
    }
  }

  @media ${mediaXlMin} {
    display: none;
  }
`;

const BackButton = styled(FlexAlignCenter)<{ $color: string }>`
  gap: 6px;
  color: ${({ $color }) => $color};

  .angle-icon {
    fill: ${({ $color }) => $color};
    transform: rotate(90deg);
  }
`;

const SeeDetailsButton = styled(FlexAlignCenter)<{ $isPanelVisible: boolean; $color: string }>`
  color: ${({ $color }) => $color};
  cursor: pointer;
  margin-left: auto;

  .angle-icon {
    fill: ${({ $color }) => $color};
    transition: transform 0.3s;
    ${({ $isPanelVisible }) => $isPanelVisible && 'transform: rotate(180deg)'};
  }
`;

const ArchiveButton = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  height: 17px;
  border-radius: 6px;
  box-sizing: initial;

  ${mediaMMdMin} {
    border: 1px solid #dbdfe5;
  }
`;

// TODO same is in details
const ResponseRate = ({ rate }) => (
  <ResponseRateWrapper>
    <Badge
      text={`${rate}`}
      color={responseLabelColor(rate)}
      darkText={(parseInt(rate, 10) >= 70 && parseInt(rate, 10) <= 84) || rate?.toLowerCase() === 'n/a'}
    />
  </ResponseRateWrapper>
);

const responseLabelColor = (rateString: string) => {
  let label = '#f4c064';

  if (rateString?.toLowerCase() === 'n/a') return 'white';

  const rate = parseInt(rateString, 10);
  if (rate >= 85) {
    label = '#2a8401';
  }
  if (rate >= 70 && rate <= 84) {
    label = '#f4c064';
  }
  if (rate < 70) {
    label = '#bc350a';
  }
  return label;
};

const ResponseRateWrapper = styled.div`
  div {
    padding: 0 4px;
  }
`;
